/**
 * A last resort for the visitor whose browser never runs the app.
 *
 * On 03/11/2026 a worker on Android opened «Completa la candidatura» from the e-mail and got the
 * page the server renders, logo and title, and nothing else: no login form, no PostHog event, not
 * one of the 19 links we had sent him used. The same worker had applied from our site the days
 * before, so the app runs on his phone in one browser and not in the one his mail app opens. Two
 * candidates, neither provable from our side: the in-app browser is older than what Next 16
 * targets (Chrome 111), or the network blocks `*.vercel.app`, where every chunk is served from
 * (`assetPrefix` in next.config.js). Either way the bundle never starts, so nothing inside it can
 * notice, help the visitor or tell us.
 *
 * This script lives in the document head instead, written in ES5 so the oldest WebView parses it.
 * It records every script that fails to load and every uncaught error, and if the app has not
 * said it booted after {@link BOOT_FALLBACK_DELAY_MS}, it shows a plain message with a way out
 * (open the page in Chrome, or retry) and sends one log line to the O2 `_rumlog` stream with the
 * user agent and what it saw. The log is what tells the two causes apart:
 *
 *   SELECT ua, script_errors, js_errors, count(*) FROM "_rumlog"
 *   WHERE message = 'boot_fallback_shown' GROUP BY ua, script_errors, js_errors
 *
 * A slow connection can still be loading at that point: when the app does boot later,
 * {@link markAppBooted} takes the message away and a second line (`boot_fallback_recovered`)
 * says so, so a late start is counted rather than mistaken for a failure.
 *
 * Both links put the magic-link token back. On `/login` the bootstrap script before this one has
 * already taken `?token=` out of the address bar and left it in `window.__rwLoginToken`, so a
 * link built from `location` alone would reopen the login page without the credential, which is
 * the arrival this fallback exists for. The token goes only into those links, never into the log.
 *
 * Wrapped in try/catch throughout: it runs before anything else and must never be the reason a
 * page fails.
 */

import { EARLY_TOKEN_KEY, LOGIN_TOKEN_PARAM } from '@restworld/feature-authentication';

export const BOOT_FALLBACK_DELAY_MS = 8000;

/** Set by {@link markAppBooted}; the head script reads it. */
const BOOTED_KEY = '__rwAppBooted';
/** The head script's own state, shared with {@link markAppBooted}. */
const STATE_KEY = '__rwBootWatchdog';
const OVERLAY_ID = 'rw-boot-fallback';

export interface BootWatchdogConfig {
  /** O2 host, as the RUM SDK gets it (`NEXT_PUBLIC_OPENOBSERVE_SITE`). */
  site?: string;
  organizationIdentifier?: string;
  /** The public RUM client token. Without it the message still shows and nothing is sent. */
  clientToken?: string;
  service: string;
  env?: string;
  version?: string;
}

interface WatchdogState {
  shown: boolean;
  send: (message: string) => void;
}

type WindowWithWatchdog = Window & { [BOOTED_KEY]?: boolean; [STATE_KEY]?: WatchdogState };

/** The texts, in the two locales the worker app serves. Plain strings: they go into ES5 source. */
const COPY = {
  it: {
    title: 'La pagina non si è caricata',
    body:
      'Il browser che stai usando non riesce ad aprire Restworld. Apri il link con Chrome, ' +
      'oppure aggiorna Chrome e Android System WebView dal Play Store e riprova.',
    chrome: 'Apri in Chrome',
    retry: 'Riprova',
  },
  en: {
    title: 'The page did not load',
    body:
      'The browser you are using cannot open Restworld. Open the link in Chrome, ' +
      'or update Chrome and Android System WebView from the Play Store and try again.',
    chrome: 'Open in Chrome',
    retry: 'Try again',
  },
};

/** The O2 logs intake the browser-logs SDK posts to (endpointBuilder.js in @openobserve/browser-core). */
function logsEndpoint({ site, organizationIdentifier, clientToken }: BootWatchdogConfig): string {
  if (!site || !organizationIdentifier || !clientToken) return '';
  return (
    `https://${site}/rum/v1/${encodeURIComponent(organizationIdentifier)}/logs` +
    `?o2source=browser&o2-api-key=${encodeURIComponent(clientToken)}&o2-evp-origin=browser`
  );
}

/**
 * The head script, as a string to inline in `_document`. Built at render time so the O2 settings
 * come from the same public env vars as the RUM SDK.
 */
export function buildBootWatchdogScript(config: BootWatchdogConfig): string {
  const settings = JSON.stringify({
    endpoint: logsEndpoint(config),
    service: config.service,
    env: config.env || 'development',
    version: config.version || 'dev',
    delay: BOOT_FALLBACK_DELAY_MS,
    copy: COPY,
  });
  // ES5 only below: no arrow functions, let/const, template literals or spread.
  return `(function(){try{
var S=${settings};
var w=window,d=document,started=new Date().getTime();
var scriptErrors=[],jsErrors=[];
var state={shown:false,send:send};
w.${STATE_KEY}=state;
w.addEventListener('error',function(e){try{
var t=e&&e.target;
if(t&&t.tagName==='SCRIPT'){var s=t.src||'';if(scriptErrors.length<10)scriptErrors.push(s.replace(/^https?:\\/\\//,'').split('?')[0]);return;}
if(jsErrors.length<10)jsErrors.push(String((e&&e.message)||'error').slice(0,200));
}catch(x){}},true);
function send(message){try{
if(!S.endpoint)return;
var line=JSON.stringify({date:new Date().getTime(),message:message,status:'warn',origin:'boot_watchdog',
service:S.service,env:S.env,version:S.version,view:{url:location.protocol+'//'+location.host+location.pathname,referrer:d.referrer?d.referrer.split('?')[0]:''},
ua:navigator.userAgent,waited_ms:new Date().getTime()-started,next_runtime:!!w.next,
script_errors:scriptErrors,js_errors:jsErrors});
if(navigator.sendBeacon&&navigator.sendBeacon(S.endpoint,line))return;
var r=new XMLHttpRequest();r.open('POST',S.endpoint,true);r.setRequestHeader('Content-Type','text/plain');r.send(line);
}catch(x){}}
function show(){try{
if(w.${BOOTED_KEY}||state.shown||!d.body)return;
state.shown=true;
var lang=(d.documentElement.lang||'it').slice(0,2)==='en'?'en':'it',c=S.copy[lang];
var url=location.protocol+'//'+location.host+location.pathname+location.search,tok=w.${EARLY_TOKEN_KEY};
if(tok&&!/[?&]${LOGIN_TOKEN_PARAM}=/.test(location.search))url+=(location.search?'&':'?')+'${LOGIN_TOKEN_PARAM}='+encodeURIComponent(tok);
url+=location.hash;
var box=d.createElement('div');box.id='${OVERLAY_ID}';box.setAttribute('role','alert');
box.style.cssText='position:fixed;top:0;left:0;right:0;bottom:0;z-index:2147483647;background:#f6f5ff;display:flex;align-items:center;justify-content:center;padding:24px;font-family:Inter,-apple-system,Roboto,Helvetica,Arial,sans-serif;color:#1c1327';
var card=d.createElement('div');card.style.cssText='max-width:400px;background:#fff;border-radius:16px;padding:28px 24px;text-align:center';
var h=d.createElement('p');h.style.cssText='font-size:20px;font-weight:700;margin:0 0 12px';h.appendChild(d.createTextNode(c.title));
var p=d.createElement('p');p.style.cssText='font-size:15px;line-height:1.5;margin:0 0 24px';p.appendChild(d.createTextNode(c.body));
card.appendChild(h);card.appendChild(p);
var btn='display:block;width:100%;box-sizing:border-box;padding:14px;border-radius:999px;font-size:16px;font-weight:600;text-decoration:none;margin-top:12px;';
if(/Android/i.test(navigator.userAgent)){
var a=d.createElement('a');a.style.cssText=btn+'background:#56309a;color:#fff';
a.href='intent://'+url.replace(/^https?:\\/\\//,'').split('#')[0]+'#Intent;scheme=https;package=com.android.chrome;end';
a.appendChild(d.createTextNode(c.chrome));card.appendChild(a);}
var b=d.createElement('a');b.href=url;b.style.cssText=btn+'border:1px solid #56309a;color:#56309a';
b.appendChild(d.createTextNode(c.retry));card.appendChild(b);
box.appendChild(card);d.body.appendChild(box);
send('boot_fallback_shown');
}catch(x){}}
setTimeout(show,S.delay);
}catch(e){}})();`;
}

/**
 * Tell the head script the app is up. Call it once the first render has committed (an effect in
 * `_app`): from then on the fallback never shows, and if it already did, it goes away and the
 * late start is logged.
 */
export function markAppBooted(): void {
  if (typeof window === 'undefined') return;
  const w = window as WindowWithWatchdog;
  w[BOOTED_KEY] = true;
  const state = w[STATE_KEY];
  if (!state?.shown) return;
  state.shown = false;
  document.getElementById(OVERLAY_ID)?.remove();
  state.send('boot_fallback_recovered');
}

/** The settings the worker app's RUM SDK uses, read the same way (see init-openobserve-rum.ts). */
export function bootWatchdogConfigFromEnv(service: string): BootWatchdogConfig {
  return {
    site: process.env.NEXT_PUBLIC_OPENOBSERVE_SITE ?? 'o2.restworld.it',
    organizationIdentifier: process.env.NEXT_PUBLIC_OPENOBSERVE_ORG ?? '',
    clientToken: process.env.NEXT_PUBLIC_OPENOBSERVE_RUM_CLIENT_TOKEN,
    service: process.env.NEXT_PUBLIC_OPENOBSERVE_SERVICE ?? service,
    env: process.env.NEXT_PUBLIC_OPENOBSERVE_ENV ?? 'development',
    version: process.env.NEXT_PUBLIC_OPENOBSERVE_VERSION || process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || 'dev',
  };
}
